import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { apiFetch } from '../lib/api';
import type { TreeCollaborator, TreeInvitation } from '@nasabtree/shared-types';

interface CollabData {
  owner: { id: string; email: string } | null;
  collaborators: (TreeCollaborator & { user: { id: string; email: string } })[];
}

export function CollaboratorsPanel({ treeId, open, onClose }: { treeId: string; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<CollabData | null>(null);
  const [invitations, setInvitations] = useState<TreeInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'editor' | 'viewer'>('viewer');
  const [inviting, setInviting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [collabRes, inviteRes] = await Promise.all([
      apiFetch<CollabData>(`/api/trees/${treeId}/collaborators`),
      apiFetch<TreeInvitation[]>(`/api/trees/${treeId}/collaborators/invitations`),
    ]);
    if (collabRes.ok) setData(collabRes.data);
    if (inviteRes.ok) setInvitations(inviteRes.data);
    setLoading(false);
  }, [treeId]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  async function handleInvite() {
    setInviting(true);
    setInviteUrl('');
    const res = await apiFetch<{ id: string; invite_url: string; email: string | null; role: string; expires_at: string }>(
      `/api/trees/${treeId}/collaborators/invite`,
      {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail || undefined, role: inviteRole }),
      },
    );
    if (res.ok) {
      setInviteUrl(res.data.invite_url);
      setInviteEmail('');
      void load();
    }
    setInviting(false);
  }

  async function handleCopyLink() {
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleRoleChange(collabId: string, role: 'editor' | 'viewer') {
    await apiFetch(`/api/trees/${treeId}/collaborators/${collabId}`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
    void load();
  }

  async function handleRemove(collabId: string) {
    await apiFetch(`/api/trees/${treeId}/collaborators/${collabId}`, { method: 'DELETE' });
    void load();
  }

  async function handleRevokeInvite(inviteId: string) {
    await apiFetch(`/api/trees/${treeId}/collaborators/invitations/${inviteId}`, { method: 'DELETE' });
    void load();
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-full max-w-lg z-50 max-h-[85vh] overflow-y-auto">
          <div className="p-6 space-y-5">
            <Dialog.Title className="text-lg font-bold text-gray-900">
              {t('collaborators.title')}
            </Dialog.Title>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Members list */}
                <div className="space-y-2">
                  {/* Owner */}
                  {data?.owner && (
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 text-xs font-bold">
                          {data.owner.email.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm text-gray-900">{data.owner.email}</p>
                        </div>
                      </div>
                      <span className="text-xs font-medium text-brand-600 bg-brand-50 px-2 py-0.5 rounded-full">
                        {t('collaborators.owner')}
                      </span>
                    </div>
                  )}

                  {/* Collaborators */}
                  {data?.collaborators.map((collab) => (
                    <div key={collab.id} className="flex items-center justify-between py-2 border-t border-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-xs font-bold">
                          {collab.user.email.charAt(0).toUpperCase()}
                        </div>
                        <p className="text-sm text-gray-900">{collab.user.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={collab.role}
                          onChange={(e) => void handleRoleChange(collab.id, e.target.value as 'editor' | 'viewer')}
                          className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        >
                          <option value="editor">{t('collaborators.editor')}</option>
                          <option value="viewer">{t('collaborators.viewer')}</option>
                        </select>
                        <button
                          onClick={() => void handleRemove(collab.id)}
                          className="text-red-500 hover:text-red-700 p-1"
                          title={t('common.delete')}
                        >
                          <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
                            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L6.94 8l-1.72 1.72a.75.75 0 1 0 1.06 1.06L8 9.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L9.06 8l1.72-1.72a.75.75 0 0 0-1.06-1.06L8 6.94 6.28 5.22Z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pending invitations */}
                {invitations.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      {t('collaborators.pending')}
                    </h3>
                    {invitations.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between py-1.5 text-sm">
                        <span className="text-gray-500">{inv.email || t('collaborators.linkInvite')}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">{inv.role}</span>
                          <button
                            onClick={() => void handleRevokeInvite(inv.id)}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            {t('collaborators.revoke')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Invite section */}
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    {t('collaborators.invite')}
                  </h3>
                  <div className="space-y-3">
                    <input
                      type="email"
                      dir="ltr"
                      placeholder={t('collaborators.emailPlaceholder')}
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <div className="flex gap-2">
                      <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value as 'editor' | 'viewer')}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="viewer">{t('collaborators.viewer')}</option>
                        <option value="editor">{t('collaborators.editor')}</option>
                      </select>
                      <button
                        onClick={() => void handleInvite()}
                        disabled={inviting}
                        className="flex-1 bg-brand-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors"
                      >
                        {inviting ? t('common.loading') : t('collaborators.createLink')}
                      </button>
                    </div>

                    {inviteUrl && (
                      <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                        <p className="text-xs text-green-700 mb-2">{t('collaborators.linkReady')}</p>
                        <div className="flex gap-2">
                          <input
                            readOnly
                            value={inviteUrl}
                            dir="ltr"
                            className="flex-1 text-xs border border-green-200 rounded px-2 py-1.5 bg-white text-gray-700"
                          />
                          <button
                            onClick={() => void handleCopyLink()}
                            className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                          >
                            {copied ? t('collaborators.copied') : t('collaborators.copy')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <Dialog.Close asChild>
            <button className="absolute top-4 end-4 text-gray-400 hover:text-gray-600" aria-label="Close">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
